import type text from './en/client.json';
import { matchLanguage } from './_helpers/matchLanguage';
import { pickDefaultLanguage } from './_helpers/pickDefaultLanguage';

type TDictionary = typeof text;

/**
 * Technical source locale: edited in the repository, pushed to Crowdin, bundled statically. It only fills the keys
 * a synced language has not translated yet and is never offered or set as the UI language.
 */
export const fallbackLanguage = 'en';

/** Target language the app prefers when nothing is saved or detected. */
const PREFERRED_LANGUAGE = 'en-GB';

/** Key of the user's language choice in the profile settings. */
export const LANGUAGE_SETTING_KEY = 'language';

/** localStorage key of the language last used in this browser (the i18next detector default). */
export const LANGUAGE_CACHE_KEY = 'i18nextLng';

const LOCALE_PATH = /^\.\/([^/]+)\/client\.json$/;

/**
 * Every synced language becomes its own chunk, loaded on demand. The directories come from
 * `yarn translation:sync`, which follows the target languages of the Crowdin project.
 * The source locale is excluded by a literal pattern because glob patterns cannot be computed.
 */
export const localeLoaders = import.meta.glob<{ default: TDictionary }>(['./*/client.json', '!./en/client.json']);

/** Crowdin target languages, derived from the synced directories: the only languages users can choose. */
export const languages: readonly string[] = Object.keys(localeLoaders)
  .map((path) => path.replace(LOCALE_PATH, '$1'))
  .sort();

/** Languages i18next may resolve: the targets plus the source locale as the fallback for untranslated keys. */
export const supportedLanguages: readonly string[] = [fallbackLanguage, ...languages];

/** UI language when nothing is saved or detected: en-GB when synced, else the first target; the source only without any target. */
export const defaultLanguage = pickDefaultLanguage(languages, PREFERRED_LANGUAGE, fallbackLanguage);

/** True for a language users can choose; a saved value failing this check (the source locale, a pruned language) counts as unset. */
export const isLanguage = (candidate: unknown): candidate is string => typeof candidate === 'string' && languages.includes(candidate);

/** Maps a browser language onto the targets (en, en-US → en-GB; de-AT → de); null when nothing matches. */
export const resolveLanguage = (candidate: unknown): string | null => matchLanguage(candidate, languages, defaultLanguage);
